import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useUserProfile } from './useUserProfile';
import { toast } from 'sonner';
import { normalizeMRR, detectRenewal } from '@/utils/mrrNormalization';
import { format } from 'date-fns';

export interface Transaction {
  id: string;
  tenant_id: string;
  customer_saas_id: string;
  payment_date: string;
  amount_paid: number;
  currency: string;
  payment_status: 'paid' | 'pending' | 'refunded' | 'failed';
  recurrence_type: 'monthly' | 'annual' | 'custom' | 'one_time' | null;
  gateway_transaction_id: string | null;
  data_source: 'api' | 'spreadsheet' | 'manual';
  description: string | null;
  notes: string | null;
  document: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  product_plan_id: string | null;
  mrr_normalized: number | null;
  coverage_months: number | null;
  period_end_date: string | null;
  is_renewal: boolean;
  previous_transaction_id: string | null;
}

export function useTransactions() {
  const { user } = useAuth();
  const { tenant } = useUserProfile();
  const queryClient = useQueryClient();

  const { data: transactions, isLoading } = useQuery({
    queryKey: ['transactions', tenant?.id],
    queryFn: async () => {
      if (!tenant?.id) return [];
      
      const { data, error } = await supabase
        .from('transactions')
        .select(`
          *,
          customer_saas:customers_saas(
            id,
            name,
            email,
            product:products!product_id(
              id,
              name
            )
          ),
          product_plan:product_plans!product_plan_id(
            id,
            name,
            recurrence_type
          )
        `)
        .eq('tenant_id', tenant.id)
        .order('payment_date', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!tenant?.id,
  });

  const createTransaction = useMutation({
    mutationFn: async (transaction: Omit<Transaction, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'tenant_id' | 'mrr_normalized' | 'coverage_months' | 'period_end_date' | 'is_renewal' | 'previous_transaction_id'> & {
      is_renewal?: boolean;
      previous_transaction_id?: string | null;
    }) => {
      if (!tenant?.id) throw new Error('Tenant não encontrado');
      
      // Buscar plano para normalização de MRR
      let normalized = null;
      if (transaction.product_plan_id) {
        const { data: plan } = await supabase
          .from('product_plans')
          .select('*')
          .eq('id', transaction.product_plan_id)
          .single();
        
      if (plan) {
        normalized = normalizeMRR(
          transaction.amount_paid,
          plan.recurrence_type as any,
          new Date(transaction.payment_date)
        );
      }
    }
    
    // OPÇÃO 1: Detecção Automática (apenas se não houver override manual)
    let finalIsRenewal = transaction.is_renewal;
    let finalPreviousTxId = transaction.previous_transaction_id;
    
    if (finalIsRenewal === undefined) {
      // Detectar renovação automaticamente
      const { data: previousTxs } = await supabase
        .from('transactions')
        .select('*')
        .eq('customer_saas_id', transaction.customer_saas_id)
        .eq('payment_status', 'paid')
        .order('payment_date', { ascending: false });
      
      const renewalInfo = transaction.product_plan_id 
        ? detectRenewal(previousTxs || [], transaction.product_plan_id)
        : { isRenewal: false, previousTxId: null };
      
      finalIsRenewal = renewalInfo.isRenewal;
      finalPreviousTxId = renewalInfo.previousTxId;
    }
    
    const { error } = await supabase
      .from('transactions')
      .insert({
        ...transaction,
        tenant_id: tenant.id,
        created_by: user?.id,
        is_renewal: finalIsRenewal,
        previous_transaction_id: finalPreviousTxId,
        mrr_normalized: normalized?.mrr_normalized || null,
        coverage_months: normalized?.coverage_months || null,
        period_end_date: normalized ? format(normalized.period_end_date, 'yyyy-MM-dd') : null,
      });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      toast.success('Transação criada com sucesso!');
    },
    onError: (error: Error) => {
      toast.error('Erro ao criar transação: ' + error.message);
    },
  });

  const updateTransaction = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Transaction> }) => {
      const { error } = await supabase
        .from('transactions')
        .update(updates)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      toast.success('Transação atualizada com sucesso!');
    },
    onError: (error: Error) => {
      toast.error('Erro ao atualizar transação: ' + error.message);
    },
  });

  const deleteTransaction = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('transactions')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      toast.success('Transação excluída com sucesso!');
    },
    onError: (error: Error) => {
      toast.error('Erro ao excluir transação: ' + error.message);
    },
  });

  return {
    transactions,
    isLoading,
    createTransaction,
    updateTransaction,
    deleteTransaction,
  };
}
